/**
 * Copyright 2021-2022 Highway9 Networks Inc.
 */
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  type EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import moment from "moment";
import { RootState, store } from "..";
import { CONNECTED, DEVICE_VIEW, IDLE } from "~/constants";
import { metricHelper } from "~/helpers/metricHelper";
import { createSocketPatchReducer } from "../events/socketPatch";
import { authenticationService, dnnService, subscriberService } from "~/services";
import { Device } from "~/types/device";
import { DeviceGroup } from "~/types/deviceGroup";
import { DeviceInfo } from "~/types/deviceInfo";
import { DeviceMetrics, DeviceMetricsData, networkMetricsList, allMetricsList, deviceRsrpMetricsList } from "~/types/DeviceMetric";
import { defaultDNN, IDNN } from "~/types/dnn";
import Radio from "~/types/radio";
import { normalizeTime } from "~/helpers/utils";
import { EventGraphData, TimeLine, TimeLineEvent, formatDeviceEventData } from "~/views/subscribers/graphs/TimelineGraphHelper";
import { defaultViewHiddenColumns } from "~/views/subscribers/useDevicePage";
import { EventQuery } from "~/types/event";
import { MetricsClient } from "~/sw/metrics/metricClient";
import { MetricsQuery } from "~/services/APIServices";
import { TransformedTimelineData } from "~/sw/fetch/device/timeline";
import { DeviceTimelineClient, DeviceTimelineClientEvents } from "~/sw/events/deviceTimelineClient";
import { radioEntities } from "./radioSlice";
import { deviceGroupEntities } from "./deviceGroupSlice";
import { isPartnerKpiOnlyUser } from "~/helpers/partnerKpiOnly";

/** Pingmon incremental-refresh window tracking. */
let lastRefreshTime = 0;
let prevDeviceID = "";
let prevInterval = 0;

export const subscriberMetricsClient1 = new MetricsClient();
export const subscriberMetricsClient2 = new MetricsClient();
export const timelineClient = new DeviceTimelineClient();

// Collator compare is ~10-50x faster than per-call localeCompare.
// The adapter is deliberately NOT sorted: a sortComparer makes every write resort
// the whole collection, and RTK does that via Object.values() on the immer draft,
// which allocates a proxy per device and reads every name back through a trap.
// Ordering is applied once in subscriberRawData instead, over plain objects and
// only when the collection actually changes (MCS-4316).
const deviceNameCollator = new Intl.Collator();
const devicesAdapter = createEntityAdapter<Device>({
  selectId: (device) => device.id!,
});

const deviceInfoAdapter = createEntityAdapter<DeviceInfo>({
  selectId: (info) => info.id!,
});

const networksAdapter = createEntityAdapter<IDNN>({
  selectId: (network) => network.id!,
});

const DEFAULT_SIMULATION = {
  selectedEdge: "",
  command: "",
  response: "",
  isRunning: false,
  operationId: "",
} as const;

type SimulationState = {
  selectedEdge: string;
  command: string;
  response: string;
  isRunning: boolean;
  operationId: string;
};

interface SubscriberState {
  open: boolean;
  loading: boolean;
  connectionQualityLoading: boolean;

  importOpen: boolean;
  detailsOpen: boolean;

  networksData: EntityState<IDNN>;
  /** Draft network being edited in the DNN form — not a selection id. */
  currentNetwork: IDNN;
  networkOpen: boolean;

  status: string;
  selectedId: string | null;
  devicesData: EntityState<Device>;
  deviceInfoData: EntityState<DeviceInfo>;
  hiddenColumns: string[];
  preset: string;

  timelineGraph: {
    eventShow: "all" | "major" | "error";
    showLogs: boolean;
    loading: boolean;
    timelineData: TimeLine;
    timelineEvents: TimeLineEvent[];
    timestamps: number[][];
    deviceEvents: EventGraphData[];
  };
  metrics: DeviceMetricsData;
  metricsSource: "device" | "radio";
  pingmon: {
    pingmonMetrics: {
      ping: unknown[];
      loss: unknown[];
      jitter: unknown[];
      min: number;
      max: number;
      average: number;
      successRate: number;
    };
    raw: unknown[];
    showLogs: boolean;
  };

  currentMetrics: Record<string, DeviceMetrics>;
  simulation: Record<string, SimulationState>;
  runningSimulationEdgeIds: string[];
}

const initialState: SubscriberState = {
  open: false,
  loading: true,
  connectionQualityLoading: false,

  importOpen: false,
  detailsOpen: false,

  networksData: networksAdapter.getInitialState(),
  currentNetwork: defaultDNN,
  networkOpen: false,

  status: "",
  selectedId: null,
  devicesData: devicesAdapter.getInitialState(),
  deviceInfoData: deviceInfoAdapter.getInitialState(),
  hiddenColumns: defaultViewHiddenColumns,
  preset: DEVICE_VIEW.DEFAULT_VIEW,

  timelineGraph: {
    eventShow: "major",
    showLogs: false,
    loading: true,
    timelineData: {},
    timelineEvents: [],
    timestamps: [],
    deviceEvents: [],
  },
  metrics: {
    device_upload_throughput: [],
    device_download_throughput: [],
    sent_bytes: [],
    received_bytes: [],
  },

  metricsSource: "device",

  pingmon: {
    pingmonMetrics: {
      ping: [],
      loss: [],
      jitter: [],
      min: 0,
      max: 0,
      average: 0,
      successRate: 0,
    },
    raw: [],
    showLogs: false,
  },

  currentMetrics: {},
  simulation: {},
  runningSimulationEdgeIds: [],
};

export const fetchDevices = createAsyncThunk(
  "subscriber/fetchDevices",
  async (_, thunk) => {
    const devices = await subscriberService.getSubscribers();
    return { devices };
  },
  // MCS-3777: backend returns 403 for RAN_KPI_ONLY for /devices.
  { condition: () => !isPartnerKpiOnlyUser() },
);

export const fetchTimelineGraph = createAsyncThunk(
  "subscriber/fetchTimelineGraph",
  async (payload: { id: string }, thunk) => {
    const state = thunk.getState() as RootState;
    const { startTime, endTime, interval } = state.utility.time;
    const params = {
      objectType: "DEVICE",
      objectId: payload.id,
      interval: {
        startTime,
        endTime: interval === "custom" ? endTime : Math.floor(Date.now() / 1000),
      },
      queryType: "DEVICE_TIMELINE",
    } as EventQuery;

    return new Promise<TransformedTimelineData>((resolve) => {
      timelineClient.initialize(
        params,
        authenticationService.currentUserValue!.token,
        (_id, _workerID, data) => {
          resolve(data as TransformedTimelineData);
        },
        { interval: 30, noPolling: interval === "custom" },
      );
    });
  },
);

export const fetchDeviceMetrics = createAsyncThunk("subscriber/fetchDeviceMetrics", async (id: string, thunk) => {
  const state = thunk.getState() as RootState;
  const { startTime, endTime, interval, timeZone } = state.utility.time;
  const resolution = metricHelper.getResolution(startTime, endTime, 500);
  const params: MetricsQuery = {
    metrics: [
      "device_upload_throughput",
      "device_download_throughput",
      "sent_bytes",
      "received_bytes",
    ],
    interval: {
      startTime: normalizeTime(startTime, resolution, timeZone),
      endTime: normalizeTime(endTime, resolution, timeZone),
    },
    resolution,
    ids: [id],
  };

  let data: DeviceMetricsData = {};
  await subscriberMetricsClient1.initialize("devices", params, authenticationService.currentUserValue!.token, (_id, metrics) => {
    data = metrics as DeviceMetricsData;
  }, { noPolling: interval === "custom" },
  );
  return data;
});

export const fetchConnectionQualityMetrics = createAsyncThunk("subscriber/fetchConnectionQualityMetrics", async (id: string, thunk) => {
  const state = thunk.getState() as RootState;
  const { startTime, endTime, interval, timeZone } = state.utility.time;
  const metricsSource = state.subscriber.metricsSource;
  const resolution = metricHelper.getResolution(startTime, endTime, 500);
  const params: MetricsQuery = {
    metrics: allMetricsList,
    interval: {
      startTime: normalizeTime(startTime, resolution, timeZone),
      endTime: normalizeTime(endTime, resolution, timeZone),
    },
    resolution,
    ids: [id],
    queryKeys: [{ key: "source", value: metricsSource }],
  };

  let data: DeviceMetricsData = {};
  await subscriberMetricsClient2.initialize("devices", params, authenticationService.currentUserValue!.token, (_id, metrics) => {
    data = metrics as DeviceMetricsData;
  }, { noPolling: interval === "custom" });
  return data;
});

function calculateNewStartTime(
  id: string,
  diff: number,
  startTime: number,
  endTime: number,
): number {
  const syncDiff = endTime - lastRefreshTime;
  if (syncDiff <= 60 && prevDeviceID === id && prevInterval === diff) {
    return lastRefreshTime;
  }
  lastRefreshTime = 0;
  return startTime;
}

export const fetchPingmon = createAsyncThunk("subscriber/fetchPingmon", async (id: string, thunk) => {
  const state = thunk.getState() as RootState;
  const { startTime, endTime, diff } = state.utility.time;
  const maxAllowedTimeInterval = 24 * 60 * 60;
  const newStartTime = calculateNewStartTime(
    id,
    Math.min(diff, maxAllowedTimeInterval),
    Math.max(startTime, endTime - maxAllowedTimeInterval),
    endTime,
  );
  const params = {
    metrics: ["pingmon"],
    interval: { startTime: newStartTime, endTime, originalStartTime: startTime },
    resolution: metricHelper.getResolution(startTime, endTime, 300),
    ids: [id],
  };
  const isFullSync = lastRefreshTime === 0;

  const data = await subscriberService.getPingmonData(params, isFullSync, state.subscriber.pingmon.raw);

  lastRefreshTime = moment(data.raw[data.raw.length - 1]?.timestamp).unix() ?? 0;
  prevDeviceID = id;
  prevInterval = diff;
  return data;
});

export const fetchDevicesLatestMetrics = createAsyncThunk(
  "subscriber/getLatestMetrics",
  async (_: void, thunk) => {
    // Hidden tab: nobody sees the data; the next 30s tick after refocus refreshes it
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return {};

    const state = thunk.getState() as RootState;
    const devices = devicesAdapter.getSelectors().selectAll(state.subscriber.devicesData);
    const deviceIds = devices
      .filter((d) => d.runtimeInfo?.status === CONNECTED)
      .map((d) => d.id) as string[];
    if (deviceIds.length === 0) return {};

    return await subscriberService.getLatestMetricSync(deviceIds);
  },
);

export const fetchDeviceRsrpMetrics = createAsyncThunk(
  "subscriber/getLatestRsrpMetrics",
  async (deviceIds: string[]) => {
    if (deviceIds.length === 0) return {};

    return await subscriberService.getLatestMetricSync(deviceIds, [...deviceRsrpMetricsList]);
  },
);

export const fetchDNN = createAsyncThunk("subscriber/fetchDNN", async () =>
  await dnnService.getDnn(),
);

export const fetchSubscriberDeviceInfo = createAsyncThunk("subscriber/fetchSubscriberDeviceInfo", async () => subscriberService.getSubscribersDeviceInfo());

export const refreshDeviceInfoForDevice = createAsyncThunk(
  "subscriber/refreshDeviceInfoForDevice",
  async (deviceId: string) => {
    try {
      await subscriberService.refreshSubscriberDeviceInfo(deviceId);

      const [devices, deviceInfoData] = await Promise.all([
        subscriberService.getSubscribers(),
        subscriberService.getSubscribersDeviceInfo(),
      ]);

      return { devices, deviceInfoData };
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
);

export const selectSimulationByDeviceId = (state: RootState, deviceId: string) =>
  state.subscriber.simulation[deviceId] ?? { ...DEFAULT_SIMULATION };

const clearMetricsKeys = (metrics: DeviceMetricsData, keys: readonly string[]) => {
  for (const key of keys) {
    delete metrics[key];
  }
};

// Full point-wise sameness: endpoint sampling would miss a different device's
// series on the same time grid (numeric compares, so this stays cheap)
const metricSeriesEqual = (a: DeviceMetricsData[string], b: DeviceMetricsData[string]) => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i][0] !== b[i][0] || a[i][1] !== b[i][1]) return false;
  }
  return true;
};

// Same result as clear-all + merge-non-null, but unchanged series keep their
// identity so memoized chart consumers skip rebuilds
const reconcileMetrics = (target: DeviceMetricsData, source: DeviceMetricsData, keys: readonly string[]) => {
  const allKeys = new Set([...keys, ...Object.keys(source)]);
  for (const key of allKeys) {
    const incoming = source[key];
    if (!incoming?.some((point) => point[1] !== null)) {
      delete target[key];
      continue;
    }
    if (!metricSeriesEqual(target[key], incoming)) {
      target[key] = incoming;
    }
  }
};

// Polls restamp `timestamp` every cycle and nothing reads it, so it is ignored;
// value-equal polls keep the stored reference and skip downstream re-enrichment
const latestMetricsEqual = (prev: DeviceMetrics | undefined, next: DeviceMetrics): boolean => {
  if (!prev) return false;
  const prevKeys = Object.keys(prev);
  const nextKeys = Object.keys(next);
  if (prevKeys.length !== nextKeys.length) return false;
  for (const key of nextKeys) {
    if (key !== "timestamp" && prev[key] !== next[key]) return false;
  }
  return true;
};

/**
 * Drop currentMetrics rows whose device is gone. Only safe against a full device list: the metrics
 * poll itself covers connected devices only, so pruning against it would discard the last known
 * values for anything merely offline (MCS-4316).
 */
const pruneMetricsToDevices = (state: SubscriberState, devices: { id?: string }[]) => {
  const liveIds = new Set(devices.map((device) => device.id));
  for (const id of Object.keys(state.currentMetrics)) {
    if (!liveIds.has(id)) {
      delete state.currentMetrics[id];
    }
  }
};

/** Overlay a full latest-metrics poll onto currentMetrics (replace). */
const replaceLatestMetrics = (state: SubscriberState, incoming: Record<string, DeviceMetrics>) => {
  for (const id of Object.keys(incoming)) {
    if (!latestMetricsEqual(state.currentMetrics[id], incoming[id])) {
      state.currentMetrics[id] = incoming[id];
    }
  }
};

/** Overlay an RSRP-only poll onto currentMetrics (patch RSRP keys). */
const patchRsrpMetrics = (state: SubscriberState, incoming: Record<string, DeviceMetrics>) => {
  for (const id of Object.keys(incoming)) {
    const next = incoming[id];
    const merged: DeviceMetrics = { ...state.currentMetrics[id], ...next };
    for (const key of deviceRsrpMetricsList) {
      if (!(key in next)) {
        delete merged[key];
      }
    }
    if (!latestMetricsEqual(state.currentMetrics[id], merged)) {
      state.currentMetrics[id] = merged;
    }
  }
};

const subscriberSlice = createSlice({
  name: "subscriber",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    /** Store selection identity only; resolve via subscriberSelected. */
    setSelected: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setCurrentNetworkValues: (state, action: PayloadAction<IDNN>) => {
      state.currentNetwork = action.payload;
    },
    setNetworkOpen: (state, action: PayloadAction<boolean>) => {
      state.networkOpen = action.payload;
    },
    setImportOpen: (state, action: PayloadAction<boolean>) => {
      state.importOpen = action.payload;
    },
    setDetailsOpen: (state, action: PayloadAction<boolean>) => {
      state.detailsOpen = action.payload;
    },
    setHiddenColumns: (state, action: PayloadAction<string[]>) => {
      state.hiddenColumns = action.payload;
    },
    setPreset: (state, action: PayloadAction<string>) => {
      state.preset = action.payload;
    },
    setTimelineEventShow: (state, action: PayloadAction<"all" | "major" | "error">) => {
      state.timelineGraph.eventShow = action.payload;
    },
    toggleTimelineLogs: (state) => {
      state.timelineGraph.showLogs = !state.timelineGraph.showLogs;
    },

    togglePingmonLogs: (state) => {
      state.pingmon.showLogs = !state.pingmon.showLogs;
    },

    setMetricsSource: (state, action: PayloadAction<"device" | "radio">) => {
      state.metricsSource = action.payload;
    },

    setSimulationState: (
      state,
      action: PayloadAction<{ deviceId: string; updates: Partial<SimulationState> }>
    ) => {
      const { deviceId, updates } = action.payload;
      state.simulation[deviceId] = {
        ...state.simulation[deviceId],
        ...updates,
      };
    },
    setBusyEdge: (state, action: PayloadAction<{ edgeId: string }>) => {
      if (!state.runningSimulationEdgeIds.includes(action.payload.edgeId)) {
        state.runningSimulationEdgeIds.push(action.payload.edgeId);
      }
    },
    clearBusyEdge: (state, action: PayloadAction<{ edgeId: string }>) => {
      state.runningSimulationEdgeIds = state.runningSimulationEdgeIds.filter(
        (id) => id !== action.payload.edgeId,
      );
    },
   
    applySocketPatchesDevices: createSocketPatchReducer(devicesAdapter, (s) => s.devicesData),
    applySocketPatchesDeviceInfo: createSocketPatchReducer(deviceInfoAdapter, (s) => s.deviceInfoData),
    applySocketPatchesNetworks: createSocketPatchReducer(networksAdapter, (s) => s.networksData),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDevices.fulfilled, (state, action) => {
        devicesAdapter.setAll(state.devicesData, action.payload.devices);
        pruneMetricsToDevices(state, action.payload.devices);
        state.status = "devices_loaded";
      })
      .addCase(fetchDevices.rejected, (state) => {
        state.status = "error_fetching_devices";
      })
      // Network DNN
      .addCase(fetchDNN.fulfilled, (state, action) => {
        networksAdapter.setAll(state.networksData, action.payload);
        state.loading = false;
        state.status = "dnn_loaded";
      })
      .addCase(fetchDNN.rejected, (state) => {
        state.status = "error_fetching_DNN";
      })

      // Timeline Graph Data
      .addCase(fetchTimelineGraph.pending, (state) => {
        state.timelineGraph.loading = true;
      })
      .addCase(fetchTimelineGraph.fulfilled, (state, action) => {
        const { timelineData, timeEvents, timestamps, events } = action.payload;
        state.timelineGraph.timestamps = timestamps;
        state.timelineGraph.timelineData = timelineData;
        state.timelineGraph.timelineEvents = timeEvents;
        state.timelineGraph.deviceEvents = formatDeviceEventData(events);
        state.timelineGraph.loading = false;
        state.status = "timeline_graph_loaded";
      })
      .addCase(fetchTimelineGraph.rejected, (state) => {
        state.status = "error_fetching_timeline_graph";
      })

      // Throughput Graph Data
      .addCase(fetchDeviceMetrics.fulfilled, (state, action) => {
        for (const key of Object.keys(action.payload)) {
          if (!metricSeriesEqual(state.metrics[key], action.payload[key])) {
            state.metrics[key] = action.payload[key];
          }
        }
        state.status = "throughput_graph_loaded";
      })
      .addCase(fetchDeviceMetrics.rejected, (state) => {
        clearMetricsKeys(state.metrics, networkMetricsList);
        state.status = "error_fetching_throughput_graph";
      })

      .addCase(fetchConnectionQualityMetrics.pending, (state) => {
        state.connectionQualityLoading = true;
        clearMetricsKeys(state.metrics, allMetricsList);
      })
      .addCase(fetchConnectionQualityMetrics.fulfilled, (state, action) => {
        reconcileMetrics(state.metrics, action.payload, allMetricsList);
        state.connectionQualityLoading = false;
        state.status = "connection_quality_graph_loaded";
      })
      .addCase(fetchConnectionQualityMetrics.rejected, (state) => {
        state.connectionQualityLoading = false;
        clearMetricsKeys(state.metrics, allMetricsList);
        state.status = "error_fetching_connection_quality_graph";
      })

      .addCase(fetchDevicesLatestMetrics.fulfilled, (state, action) => {
        replaceLatestMetrics(state, action.payload ?? {});
      })
      .addCase(fetchDeviceRsrpMetrics.fulfilled, (state, action) => {
        patchRsrpMetrics(state, action.payload ?? {});
      })
      .addCase(fetchPingmon.fulfilled, (state, action) => {
        const { data, raw } = action.payload;
        state.pingmon.pingmonMetrics = data;
        state.pingmon.raw = raw;
      })
      .addCase(fetchPingmon.rejected, (state) => {
        state.status = "error_fetching_pingmon";
      })
      .addCase(fetchSubscriberDeviceInfo.fulfilled, (state, action) => {
        deviceInfoAdapter.setAll(state.deviceInfoData, action.payload);
      })
      .addCase(refreshDeviceInfoForDevice.fulfilled, (state, action) => {
        devicesAdapter.setAll(state.devicesData, action.payload.devices);
        pruneMetricsToDevices(state, action.payload.devices);
        deviceInfoAdapter.setAll(state.deviceInfoData, action.payload.deviceInfoData);
        state.loading = false;
        state.status = "device_info_refresh_requested";
      });
  },
});

export const subscriberActions = subscriberSlice.actions;
export default subscriberSlice.reducer;

const deviceSelectors = devicesAdapter.getSelectors(
  (state: RootState) => state.subscriber.devicesData,
);
const deviceInfoSelectors = deviceInfoAdapter.getSelectors(
  (state: RootState) => state.subscriber.deviceInfoData,
);
const networkSelectors = networksAdapter.getSelectors(
  (state: RootState) => state.subscriber.networksData,
);

export const subscriberOpen = (state: RootState) => state.subscriber.open;
export const subscriberHiddenColumns = (state: RootState) => state.subscriber.hiddenColumns;
export const subscriberPreset = (state: RootState) => state.subscriber.preset;
export const subscriberImportOpen = (state: RootState) => state.subscriber.importOpen;
export const subscriberDetailsOpen = (state: RootState) => state.subscriber.detailsOpen;

export const subscriberNetworksData = networkSelectors.selectAll;
export const subscriberNetworkEntities = networkSelectors.selectEntities;
export const subscriberCurrentNetwork = (state: RootState) => state.subscriber.currentNetwork;
export const subscriberLoading = (state: RootState) => state.subscriber.loading;
export const subscriberConnectionQualityLoading = (state: RootState) =>
  state.subscriber.connectionQualityLoading;
export const subscriberNetworkOpen = (state: RootState) => state.subscriber.networkOpen;

/** Raw entity selectors — no radio-join / group-name enrichment. */
export const subscriberRawData = createSelector([deviceSelectors.selectAll], (devices) =>
  [...devices].sort((a, b) => deviceNameCollator.compare(a.name, b.name)),
);
export const subscriberRawEntities = deviceSelectors.selectEntities;

export const subscriberTimelineGraphLoading = (state: RootState) => state.subscriber.timelineGraph.loading;
export const subscriberDeviceInfoData = deviceInfoSelectors.selectAll;
export const subscriberDeviceInfoEntities = deviceInfoSelectors.selectEntities;

export const subscriberTimelineData = (state: RootState) =>
  state.subscriber.timelineGraph.timelineData;
export const subscriberTimelineEvents = (state: RootState) =>
  state.subscriber.timelineGraph.timelineEvents;

export const subscriberMetric =
  (metric: string) => (state: RootState) => state.subscriber.metrics[metric];
export const pingmonMetrics = (state: RootState) => state.subscriber.pingmon.pingmonMetrics;
export const pingmonRawData = (state: RootState) => state.subscriber.pingmon.raw;

/**
 * Per-group device counts derived from the entity collection (not stored in slice).
 * Recomputed whenever devices change so group tooling stays in sync without a
 * separate mapping write on fetch.
 */
const subscriberDeviceGroupCounts = createSelector(
  [subscriberRawData],
  (devices) => {
    const deviceGroupMappingID: Record<string, number> = {};
    const connectedDeviceGroups: Record<string, number> = {};

    for (const device of devices) {
      const groupId = device.groupId;
      if (!groupId) continue;

      deviceGroupMappingID[groupId] = (deviceGroupMappingID[groupId] ?? 0) + 1;

      const status = device.runtimeInfo?.status;
      if (status === CONNECTED || status === IDLE) {
        connectedDeviceGroups[groupId] = (connectedDeviceGroups[groupId] ?? 0) + 1;
      }
    }

    return { deviceGroupMappingID, connectedDeviceGroups };
  },
);

export const subscriberDeviceGroupsMapping = createSelector(
  [subscriberDeviceGroupCounts],
  (counts) => counts.deviceGroupMappingID,
);
export const subscriberConnectedDeviceGroups = createSelector(
  [subscriberDeviceGroupCounts],
  (counts) => counts.connectedDeviceGroups,
);

export const subscriberMetricsError = (state: RootState) => state.subscriber.status.startsWith("error");
export const subscriberMetricsSource = (state: RootState) => state.subscriber.metricsSource;

// Memoized - as a plain selector this scanned every series on every dispatch
export const subscriberHasMetricsData = createSelector(
  [(state: RootState) => state.subscriber.metrics],
  (metrics) =>
    allMetricsList.some((metricKey) => {
      const metricData = metrics[metricKey];
      return !!metricData?.some((point) => point[1] !== null);
    }),
);

/**
 * Derived selector: radio / device-group joins and latest-metrics overlay stay fresh
 * without writing client-only `_metrics` back onto the entity collection.
 * Output array identity is reused when every element is unchanged, so source
 * flushes that touch nothing relevant are no-ops downstream.
 */
export const subscriberData = createSelector(
  [
    subscriberRawData,
    radioEntities,
    deviceGroupEntities,
    (state: RootState) => state.subscriber.currentMetrics,
  ],
  (devices, radios, groups, currentMetrics) =>
    enrichDevicesWithRadioAndGroup(devices, radios, groups, currentMetrics),
  { memoizeOptions: { resultEqualityCheck: (a: Device[], b: Device[]) => a.length === b.length && a.every((device, i) => device === b[i]) } },
);

export const subscriberEntities = createSelector(
  [subscriberData], (enrichedDevices) =>
    Object.fromEntries(
      enrichedDevices.filter((device) => device.id).map((device) => [device.id!, device]),
    ) as Record<string, Device>,
);

/**
 * Selected device with radio-join enrichment (radioApName / dual-carrier cell).
 * DeviceSummary Runtime Info (Radio AP, Site, Radio Group) depends on these fields.
 * Enriches just the one device instead of materializing the 10k-entry map.
 */
export const subscriberSelected = createSelector(
  [
    (state: RootState) => state.subscriber.selectedId,
    subscriberRawEntities,
    radioEntities,
    deviceGroupEntities,
    (state: RootState) => state.subscriber.currentMetrics,
  ],
  (selectedId, byId, radios, groups, currentMetrics): Device | null => {
    const raw = selectedId ? byId[selectedId] : undefined;
    if (!raw) return null;
    return enrichDevicesWithRadioAndGroup([raw], radios, groups, currentMetrics)[0] ?? null;
  },
);

export const deviceConnectedToTechnology = createSelector(
  [subscriberSelected, radioEntities],
  (device, radiosById) => {
    const radioId = device?.runtimeInfo?.radioId;
    const radio = radioId ? (radiosById[radioId] ?? null) : null;
    return device?.runtimeInfo?.connectedNetworkType ?? radio?.technology ?? "4G";
  },
);


/**
 * Enriches devices with radio-join fields, group name, and latest polled metrics.
 *
 * Fields computed here (not stored in entity slice):
 *  - runtimeInfo.radioApName   — resolved from radioId → radio.name (with cellId fallback)
 *  - runtimeInfo.radioID       — radioId || radioApId
 *  - runtimeInfo.radioCellNumber — dual-carrier cell index
 *  - _deviceGroupName          — deviceGroups[groupId].name
 *  - _metrics                  — latest poll from currentMetrics (table KPIs + RSRP; survives devices setAll)
 */
// Per-device enrichment cache keyed on the raw entity object. Unchanged devices
// keep their enriched identity across flushes; without this every flush
// reallocates all 10k+ devices and re-renders the whole table (same pattern as
// processedRadioCache in radioHelper).
type EnrichEntry = { radios: unknown; groups: unknown; metrics: DeviceMetrics | undefined; result: Device };
const enrichedDeviceCache = new WeakMap<Device, EnrichEntry>();

let radioByCellIdCacheSource: unknown = null;
let radioByCellIdCache: Map<string, Radio> | null = null;

function getRadioByCellId(radiosById: Record<string, Radio | undefined>): Map<string, Radio> {
  if (radioByCellIdCacheSource !== radiosById || !radioByCellIdCache) {
    radioByCellIdCache = new Map<string, Radio>();
    for (const radio of Object.values(radiosById)) {
      if (!radio) continue;
      for (const cell of radio.cellParameters ?? []) {
        if (cell.cellId != null) {
          radioByCellIdCache.set(String(cell.cellId), radio);
        }
      }
    }
    radioByCellIdCacheSource = radiosById;
  }
  return radioByCellIdCache;
}

// True when both enriched views of the SAME raw device carry the same joined fields
function enrichedEquivalent(a: Device, b: Device): boolean {
  return (
    (a as Device & { _deviceGroupName?: string })._deviceGroupName ===
      (b as Device & { _deviceGroupName?: string })._deviceGroupName &&
    a._metrics === b._metrics &&
    a.runtimeInfo?.radioApName === b.runtimeInfo?.radioApName &&
    a.runtimeInfo?.radioID === b.runtimeInfo?.radioID &&
    a.runtimeInfo?.radioCellNumber === b.runtimeInfo?.radioCellNumber
  );
}

function enrichDevicesWithRadioAndGroup(
  devices: Device[],
  radiosById: Record<string, Radio | undefined>,
  groupsById: Record<string, DeviceGroup | undefined>,
  currentMetrics: Record<string, DeviceMetrics> = {},
): Device[] {
  const radioByCellId = getRadioByCellId(radiosById);

  return devices.map((device) => {
    const latestMetrics = device.id ? currentMetrics[device.id] : undefined;
    const entry = enrichedDeviceCache.get(device);
    if (entry && entry.radios === radiosById && entry.groups === groupsById && entry.metrics === latestMetrics) {
      return entry.result;
    }
    const groupId = device.groupId;
    const deviceGroup = groupId ? groupsById[groupId] : undefined;

    const radioId = device.runtimeInfo?.radioId;
    const radio = radioId ? radiosById[radioId] : undefined;

    const isDualCarrierAttached = radio?.carrierMode === "DUAL_CARRIER";
    const attachedDualCarrierCell = radio?.cellParameters?.find(
      (item) => String(item.cellId) === String(device.runtimeInfo?.radioApId),
    )?.cellIndex as string | undefined;

    let radioApName = radio?.name;
    if (!radioApName && device.runtimeInfo?.radioApId) {
      radioApName = radioByCellId.get(String(device.runtimeInfo.radioApId))?.name;
    }
    if (!radioApName && device.runtimeInfo?.radioId) {
      radioApName = device.runtimeInfo.eci?.toString();
    }

    const needsRuntimePatch = radioApName || isDualCarrierAttached;
    const runtimeInfo = needsRuntimePatch && device.runtimeInfo
      ? {
        ...device.runtimeInfo,
        ...(radioApName ? {
          radioApName,
          radioID: device.runtimeInfo.radioId || device.runtimeInfo.radioApId,
        } : {}),
        ...(isDualCarrierAttached ? { radioCellNumber: attachedDualCarrierCell } : {}),
      }
      : device.runtimeInfo;

    const needsPatch = needsRuntimePatch || deviceGroup || latestMetrics;
    const enriched: Device = needsPatch
      ? {
        ...device,
        ...(deviceGroup ? { _deviceGroupName: deviceGroup.name } : {}),
        ...(runtimeInfo !== device.runtimeInfo ? { runtimeInfo } : {}),
        ...(latestMetrics ? { _metrics: latestMetrics } : {}),
      }
      : device;

    // Sources changed identity but this device's joined fields did not -
    // keep the previous enriched object so downstream memo/render is skipped
    if (entry && enrichedEquivalent(entry.result, enriched)) {
      entry.radios = radiosById;
      entry.groups = groupsById;
      entry.metrics = latestMetrics;
      return entry.result;
    }

    enrichedDeviceCache.set(device, { radios: radiosById, groups: groupsById, metrics: latestMetrics, result: enriched });
    return enriched;
  });
}


// Metrics / timeline worker → Redux bridges.
// Guard ensures these listeners are registered exactly once per module lifetime,
// even under HMR where the module body can be re-executed (MCS-perf).
let _workerBridgesRegistered = false;
setTimeout(() => {
  if (_workerBridgesRegistered) return;
  _workerBridgesRegistered = true;

  subscriberMetricsClient1.eventEmitter().on("worker:data", (_id, _workerID, metrics) => {
    store.dispatch({ type: "subscriber/fetchDeviceMetrics/fulfilled", payload: metrics });
  });
  subscriberMetricsClient2.eventEmitter().on("worker:data", (_id, _workerID, metrics) => {
    store.dispatch({
      type: "subscriber/fetchConnectionQualityMetrics/fulfilled",
      payload: metrics,
    });
  });
  subscriberMetricsClient1.eventEmitter().on("worker:error", (_id, _workerID, error) => {
    store.dispatch({ type: "subscriber/fetchDeviceMetrics/rejected", payload: error });
  });
  subscriberMetricsClient2.eventEmitter().on("worker:error", (_id, _workerID, error) => {
    store.dispatch({
      type: "subscriber/fetchConnectionQualityMetrics/rejected",
      payload: error,
    });
  });

  DeviceTimelineClientEvents.on("worker:data", async (_id, workerID, data) => {
    const currentWorkerID = await timelineClient.getWorkerID();
    if (workerID === currentWorkerID) {
      store.dispatch({ type: "subscriber/fetchTimelineGraph/fulfilled", payload: data });
    }
  });
}, 0);
